import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreateReportRequest, Framework, Site } from '@mvcp/contracts';
import { createReport, downloadBlob } from '../api';
import { useDialogA11y } from './useDialogA11y';

/** How the async framework/site lists are progressing when the modal opens. */
export type ScopeListStatus = 'loading' | 'loaded' | 'failed';

/** Today as `YYYY-MM-DD` (UTC) — the max selectable as-of date (no future). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The default framework selection for an entry point, derived from the CURRENT
 * loaded list: the preselected framework when it is actually present in that list,
 * otherwise all applicable frameworks (the whole-Company default). A preselect that
 * is hidden/inapplicable (absent from the loaded list) must NOT be posted as an
 * unknown id — it falls back to the whole-Company default.
 */
function seedSelection(frameworks: Pick<Framework, 'id'>[], preselectedFrameworkId: string | null): Set<string> {
  const ids = frameworks.map((f) => f.id);
  if (preselectedFrameworkId && ids.includes(preselectedFrameworkId)) {
    return new Set([preselectedFrameworkId]);
  }
  return new Set(ids);
}

/**
 * The shared report scope-picker (Story 6.1, UX-DR17). One centered dialog opened
 * from BOTH entry points (whole-Company "Export Report" and a Framework's "Create
 * report") — they differ only in `preselectedFrameworkId`. Clones {@link ConfirmModal}'s
 * shape + `useDialogA11y` (scrim/Esc close, focus trap, focus restore).
 *
 * It owns the scope selection: a framework checklist (pre-seeded per entry point), a
 * site checklist (none checked = all sites/vessels), and an as-of `<input type="date">`
 * (blank = current; `max=today` so a future date can't be picked). Generate shows a
 * non-blocking "Generating report (PDF)…" label while in flight, then downloads.
 *
 * Async list handling: the frameworks/sites arrive from `CockpitShell` after mount, so
 * (a) a `loading` state is shown while they resolve, (b) the default framework
 * selection is re-seeded once the list arrives (never frozen empty in the initializer),
 * and (c) a distinct `failed` state offers a retry instead of falsely claiming no
 * frameworks apply.
 */
export function ReportGeneratorModal({
  frameworks,
  sites,
  listStatus,
  preselectedFrameworkId = null,
  onRetryLoad,
  onClose,
}: {
  frameworks: Framework[];
  sites: Site[];
  listStatus: ScopeListStatus;
  /** The framework pre-selected by the "Create report" entry, or null (dashboard = all). */
  preselectedFrameworkId?: string | null;
  onRetryLoad?: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLElement>(null);

  const [frameworkSel, setFrameworkSel] = useState<Set<string>>(() =>
    seedSelection(frameworks, preselectedFrameworkId),
  );
  const [siteSel, setSiteSel] = useState<Set<string>>(() => new Set());
  const [asOf, setAsOf] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Block dismissal (scrim / Esc / Close) while a report is in flight so the modal is
  // never unmounted mid-fetch (a setState-on-unmounted after the request resolves).
  const requestClose = useCallback(() => {
    if (!submitting) onClose();
  }, [submitting, onClose]);
  useDialogA11y(panelRef, requestClose);

  // (Re)derive the default framework selection from the CURRENT loaded list every time
  // it resolves/changes — keyed off the loaded list's identity, never frozen from a
  // prior open. On reopen with a fresh list (or a list that resolves after opening) the
  // default is re-seeded from that list, so a stale prior selection can't leak through.
  const seededSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (listStatus !== 'loaded') return;
    const sig = frameworks.map((f) => f.id).join('|');
    if (seededSigRef.current === sig) return; // already seeded for this exact list
    seededSigRef.current = sig;
    setFrameworkSel(seedSelection(frameworks, preselectedFrameworkId));
  }, [frameworks, listStatus, preselectedFrameworkId]);

  function toggle(set: Set<string>, id: string): Set<string> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  }

  async function onGenerate(): Promise<void> {
    setError(null);
    setDone(false);
    setSubmitting(true);
    const input: CreateReportRequest = {
      frameworkIds: [...frameworkSel],
      siteIds: [...siteSel],
      asOf: asOf === '' ? null : asOf,
    };
    try {
      const blob = await createReport(input);
      downloadBlob(blob, `compliance-report-${asOf === '' ? todayIso() : asOf}.pdf`);
      setDone(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't generate the report. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const canGenerate = listStatus === 'loaded' && frameworkSel.size > 0 && !submitting;

  return (
    <div
      role="presentation"
      onClick={requestClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--scrim)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 'var(--z-modal)' as unknown as number,
      }}
    >
      <section
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Generate compliance report"
        onClick={(e) => e.stopPropagation()}
        className="card stack"
        style={{ width: 'min(520px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--elevation-modal)' }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Generate compliance report</h2>

        {listStatus === 'loading' && (
          <p className="muted" style={{ margin: 0 }} aria-busy="true">
            Loading frameworks and sites…
          </p>
        )}

        {listStatus === 'failed' && (
          <div className="stack" role="alert">
            <p className="muted" style={{ margin: 0 }}>
              Couldn’t load the frameworks and sites for the report.
            </p>
            {onRetryLoad && (
              <button
                type="button"
                className="btn-accent"
                data-surface="accent"
                onClick={onRetryLoad}
                style={{ alignSelf: 'flex-start' }}
              >
                Retry
              </button>
            )}
          </div>
        )}

        {listStatus === 'loaded' && frameworks.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>
            No frameworks apply to your company, so there is nothing to report.
          </p>
        )}

        {listStatus === 'loaded' && frameworks.length > 0 && (
          <>
            <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8, margin: 0, padding: 12 }}>
              <legend style={{ fontSize: 13, fontWeight: 700, padding: '0 6px' }}>Frameworks</legend>
              <div className="stack" style={{ gap: 6 }}>
                {frameworks.map((f) => (
                  <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={frameworkSel.has(f.id)}
                      onChange={() => setFrameworkSel((s) => toggle(s, f.id))}
                    />
                    <span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{f.code}</span> {f.name}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8, margin: 0, padding: 12 }}>
              <legend style={{ fontSize: 13, fontWeight: 700, padding: '0 6px' }}>Sites / vessels</legend>
              <p className="muted" style={{ margin: '0 0 6px', fontSize: 12 }}>
                None selected = all sites/vessels.
              </p>
              <div className="stack" style={{ gap: 6 }}>
                {sites.map((s) => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={siteSel.has(s.id)}
                      onChange={() => setSiteSel((prev) => toggle(prev, s.id))}
                    />
                    <span>{s.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <label className="field-label" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              As-of date (blank = current)
              <input
                type="date"
                value={asOf}
                max={todayIso()}
                onChange={(e) => setAsOf(e.target.value)}
                style={{ padding: 6, borderRadius: 6, border: '1px solid var(--border)', maxWidth: 200 }}
              />
            </label>
          </>
        )}

        <div role="alert" aria-live="assertive" style={{ minHeight: error || done ? undefined : 0 }}>
          {error && (
            <span className="status-pill" data-tone="bad">
              <span className="status-pill__dot" aria-hidden="true" />
              {error}
            </span>
          )}
          {done && !error && (
            <span className="status-pill" data-tone="ok">
              <span className="status-pill__dot" aria-hidden="true" />
              Report downloaded.
            </span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="icon-button" onClick={requestClose} disabled={submitting}>
            Close
          </button>
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={() => void onGenerate()}
            disabled={!canGenerate}
          >
            {submitting ? 'Generating report (PDF)…' : 'Generate'}
          </button>
        </div>
      </section>
    </div>
  );
}
